/**
 * « Cost, Revenue, ROAS » — répartition entre plateformes publicitaires.
 *
 * Cost et Revenue sont des parts d'un tout (combien de la dépense, combien des
 * ventes attribuées) : deux anneaux. Le ROAS n'est PAS une part — deux ROAS ne
 * s'additionnent pas —, il se compare : des bâtons, un par plateforme, et le
 * ROAS de l'ensemble en gris (recalculé sur les sommes, jamais moyenné).
 *
 * Revenue = valeur des ventes attribuées par chaque plateforme (« Conversion
 * Value »), comme dans le tableau « Performances by platform ».
 *
 * `AdsCostRatios` : CPC, CPM, CPA, en bâtons de la même façon.
 */

import { adsRoas } from '@shared/types/sales';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatMoney } from '../lib/format';
import { AD_PLATFORM_COLORS, AXIS_STYLE, BAR_RADIUS, COLORS, OTHER_COLOR } from '../lib/theme';
import type { DataColumn } from '../types';
import type { AdsKpiRow } from './AdsKpiTable';
import { ChartCard } from './Card';
import { DonutChart, type DonutSlice } from './DonutChart';

const ratioFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatRoas = (value: number) => `×${ratioFormatter.format(value)}`;

type Figures = Omit<AdsKpiRow, 'key' | 'platform'>;

interface PlatformBar {
  key: string;
  label: string;
  value: number;
  color: string;
}

function BarTooltip({
  active,
  payload,
  format,
}: {
  active?: boolean;
  payload?: Array<{ payload?: PlatformBar }>;
  format: (value: number) => string;
}) {
  const bar = payload?.[0]?.payload;
  if (!active || !bar) return null;
  return (
    <div className="tooltip" role="tooltip">
      <div className="tooltip__row">
        <span className="legend__key legend__key--area" style={{ background: bar.color }} aria-hidden="true" />
        <span className="tooltip__name">{bar.label}</span>
        <span className="tooltip__value">{format(bar.value)}</span>
      </div>
    </div>
  );
}

/**
 * Une barre par plateforme, et l'ensemble en gris quand deux sont cochées.
 * `measure` est appliquée aux chiffres BRUTS de chaque ligne puis à leur somme :
 * le ROAS (ou le CPC…) de l'ensemble est recalculé sur les sommes, jamais la
 * moyenne des barres. Une plateforme sans dénominateur (aucun clic…) n'a pas
 * de barre plutôt qu'une barre à zéro.
 */
export function PlatformBarChart({
  title,
  subtitle,
  rows,
  measure,
  format,
  emptyMessage = 'Aucune dépense publicitaire sur cette période.',
}: {
  title: string;
  subtitle: string;
  rows: readonly AdsKpiRow[];
  measure: (figures: Figures) => number | null;
  format: (value: number) => string;
  emptyMessage?: string;
}) {
  const bars: PlatformBar[] = [];
  for (const row of rows) {
    const value = measure(row);
    if (value !== null) bars.push({ key: row.key, label: row.platform, value, color: AD_PLATFORM_COLORS[row.key] });
  }
  // L'ensemble n'apporte rien quand une seule plateforme a une barre : c'est la même.
  if (bars.length > 1) {
    const total = rows.reduce<Figures>(
      (acc, row) => ({
        cost: acc.cost + row.cost,
        impressions: acc.impressions + row.impressions,
        clicks: acc.clicks + row.clicks,
        conversions: acc.conversions + row.conversions,
        conversionValue: acc.conversionValue + row.conversionValue,
      }),
      { cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0 },
    );
    const value = measure(total);
    if (value !== null) bars.push({ key: 'total', label: 'Ensemble', value, color: OTHER_COLOR });
  }

  const columns: ReadonlyArray<DataColumn<PlatformBar>> = [
    { key: 'label', header: 'Plateforme', render: (row) => row.label },
    { key: 'value', header: title, numeric: true, render: (row) => format(row.value) },
  ];
  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };

  return (
    <ChartCard title={title} subtitle={subtitle} rows={bars} columns={columns} emptyMessage={emptyMessage}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={bars} margin={{ top: 24, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} />
          <YAxis tickFormatter={format} tick={axisTick} axisLine={false} tickLine={false} width={60} domain={[0, 'auto']} />
          <Tooltip cursor={{ fill: 'rgb(255 255 255 / 0.04)' }} content={<BarTooltip format={format} />} />
          <Bar dataKey="value" name={title} radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]} maxBarSize={48} isAnimationActive={false}>
            {bars.map((bar) => (
              <Cell key={bar.key} fill={bar.color} />
            ))}
            {/* Trois barres au plus : la valeur au-dessus de chacune se lit sans survol. */}
            <LabelList
              dataKey="value"
              position="top"
              formatter={(value: unknown) => (typeof value === 'number' ? format(value) : '')}
              fill={COLORS.textSecondary}
              fontSize={12}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function AdsPlatformSplit({ rows, currency }: { rows: readonly AdsKpiRow[]; currency: string }) {
  const money = (value: number) => formatMoney(value, currency);
  const slices = (pick: (row: AdsKpiRow) => number): DonutSlice[] =>
    rows.map((row) => ({ key: row.key, label: row.platform, value: pick(row), color: AD_PLATFORM_COLORS[row.key] }));

  return (
    <>
      <DonutChart
        title="Cost (HT)"
        subtitle="Part de chaque plateforme dans la dépense"
        slices={slices((row) => row.cost)}
        formatValue={money}
        emptyMessage="Aucune dépense publicitaire sur cette période."
      />
      <DonutChart
        title="Revenue (TTC)"
        subtitle="Part de chaque plateforme dans les ventes attribuées"
        slices={slices((row) => row.conversionValue)}
        formatValue={money}
        emptyMessage="Aucune vente attribuée sur cette période."
      />
      <PlatformBarChart
        title="ROAS (HT)"
        subtitle="Ventes attribuées TTC ÷ dépense HT, par plateforme"
        rows={rows}
        measure={(f) => adsRoas(f.conversionValue, f.cost)}
        format={formatRoas}
      />
    </>
  );
}

/** « CPC, CPM, CPA » — ce que coûte un clic, mille impressions, une conversion. */
export function AdsCostRatios({ rows, currency }: { rows: readonly AdsKpiRow[]; currency: string }) {
  const unitMoney = (value: number) => formatMoney(value, currency, true);
  return (
    <>
      <PlatformBarChart
        title="CPC (HT)"
        subtitle="Dépense ÷ clics, par plateforme"
        rows={rows}
        measure={(f) => (f.clicks > 0 ? f.cost / f.clicks : null)}
        format={unitMoney}
      />
      <PlatformBarChart
        title="CPM (HT)"
        subtitle="Dépense pour 1 000 impressions, par plateforme"
        rows={rows}
        measure={(f) => (f.impressions > 0 ? (f.cost / f.impressions) * 1000 : null)}
        format={unitMoney}
      />
      <PlatformBarChart
        title="CPA (HT)"
        subtitle="Dépense ÷ conversions attribuées, par plateforme"
        rows={rows}
        measure={(f) => (f.conversions > 0 ? f.cost / f.conversions : null)}
        format={unitMoney}
      />
    </>
  );
}
